/** What the preview-frames call answered, as far as the creator's message is concerned. */
export interface PreviewResponse {
  results: Array<{ index: number; previewFramePath?: string; failed?: boolean }>;
  generated: number;
}

/** The toast a finished preview run shows, plus the shots (0-based) whose panel could not be drawn. */
export interface PreviewOutcome {
  tone: 'error' | 'info';
  title: string;
  body: string;
  failedIndexes: number[];
}

const shotList = (indexes: number[]) => {
  const n = indexes.map((i) => i + 1);
  if (n.length === 1) return `shot ${n[0]}`;
  return `shots ${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
};

/**
 * SAY WHAT THE PREVIEW DID, including when it failed (BUGS.md 2026-09-27). The server reports a panel it could
 * not draw as `failed` and never charges for it, but the client only counted `generated`, so a run where every
 * panel failed read as "Preview is up to date" and a partial failure said nothing about the missing shots.
 * Failure is named in words, with no colour, and the message states that a miss costs nothing.
 */
export function describePreviewOutcome(res: PreviewResponse): PreviewOutcome {
  const failedIndexes = res.results.filter((r) => r.failed).map((r) => r.index).sort((a, b) => a - b);
  const drew = res.generated === 1 ? 'Drew 1 panel' : `Drew ${res.generated} panels`;
  if (failedIndexes.length && !res.generated) {
    return {
      tone: 'error',
      title: 'Preview failed',
      body: `We could not draw ${shotList(failedIndexes)}. You were not charged. Try again in a moment.`,
      failedIndexes,
    };
  }
  if (failedIndexes.length) {
    return {
      tone: 'error',
      title: `${drew}, ${failedIndexes.length} failed`,
      body: `We could not draw ${shotList(failedIndexes)}, and you were not charged for them. Update the preview to try those again.`,
      failedIndexes,
    };
  }
  if (!res.generated) {
    return {
      tone: 'info',
      title: 'Preview is up to date',
      body: 'Every shot already has a panel for how it reads right now — nothing to redraw, nothing charged.',
      failedIndexes,
    };
  }
  return { tone: 'info', title: drew, body: 'Only the shots that changed were redrawn.', failedIndexes };
}
